import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import axios from "axios";
import keycloakConfig from "src/constant/keycloak";
import { useAuthMiddleware } from "src/services/globalAxios";
import { jwtDecode } from "jwt-decode";

interface KeycloakContextProps {
  initialized: boolean;
  authenticated: boolean;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

interface JwtPayload {
  exp: number;
  preferred_username?: string;
  email?: string;
  name?: string;
}

const KeycloakContext = createContext<KeycloakContextProps>({
  initialized: false,
  authenticated: false,
  token: null,
  login: async () => {},
  logout: () => {},
  refreshToken: async () => {},
});

export const KeycloakProvider = ({ children }: { children: ReactNode }) => {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  // ✅ Gắn interceptor Authorization tự động
  useAuthMiddleware(token);

  // ✅ Khi khởi động, kiểm tra token lưu trong localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("access_token");
    const expiry = localStorage.getItem("token_expiry");

    if (storedToken && expiry && Date.now() < +expiry) {
      setToken(storedToken);
      setAuthenticated(true);
    } else {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("token_expiry");
    }

    setInitialized(true);
  }, []);

  // ✅ Đăng nhập
  const login = async (username: string, password: string) => {
    try {
      console.info("🔑 Đang đăng nhập Keycloak...");

      const response = await axios.post(
        `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/token`,
        new URLSearchParams({
          grant_type: "password",
          client_id: keycloakConfig.clientId,
          username,
          password,
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );

      const { access_token, refresh_token, expires_in } = response.data;

      console.info("✅ Đăng nhập thành công:", {
        expires_in,
        access_token: access_token?.slice(0, 20) + "...",
      });

      setToken(access_token);
      setAuthenticated(true);
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("token_expiry", String(Date.now() + expires_in * 1000));
    } catch (err: any) {
      console.error("❌ Lỗi đăng nhập:", err.response?.data || err.message);
      setAuthenticated(false);
      throw new Error("Đăng nhập thất bại. Vui lòng kiểm tra tài khoản/mật khẩu.");
    }
  };

  // ✅ Refresh token
  const refreshToken = async () => {
    const refresh_token = localStorage.getItem("refresh_token");
    if (!refresh_token) return logout();

    try {
      console.info("🔁 Refresh token...");

      const response = await axios.post(
        `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/token`,
        new URLSearchParams({
          grant_type: "refresh_token",
          client_id: keycloakConfig.clientId,
          refresh_token,
        }),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );

      const { access_token, refresh_token: newRefresh, expires_in } = response.data;

      console.info("✅ Refresh thành công:", {
        expires_in,
        access_token: access_token?.slice(0, 20) + "...",
      });

      setToken(access_token);
      setAuthenticated(true);
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", newRefresh);
      localStorage.setItem("token_expiry", String(Date.now() + expires_in * 1000));
    } catch (err: any) {
      console.error("❌ Refresh thất bại:", err.response?.data || err.message);
      logout();
    }
  };

  // ✅ Logout
  const logout = () => {
    console.info("🚪 Đăng xuất...");
    setToken(null);
    setAuthenticated(false);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("token_expiry");
  };

  // ✅ Decode token cục bộ để kiểm tra hết hạn
  const verifyToken = (accessToken: string): boolean => {
    try {
      const decoded = jwtDecode<JwtPayload>(accessToken);
      const now = Date.now() / 1000;
      if (decoded.exp < now) {
        console.warn("⚠️ Token hết hạn theo exp trong payload");
        return false;
      }
      return true;
    } catch (err) {
      console.warn("⚠️ Token không hợp lệ:", err);
      return false;
    }
  };

  // Tự động kiểm tra token mỗi phút
  useEffect(() => {
    const interval = setInterval(async () => {
      const expiry = localStorage.getItem("token_expiry");

      // Refresh sớm 5 phút trước khi hết hạn
      if (expiry && Date.now() > +expiry - 5 * 60_000) {
        console.log("Token sắp hết hạn → refresh");
        await refreshToken();
      }

      // Kiểm tra exp trong token
      if (token && !verifyToken(token)) {
        console.warn("Token hết hạn hoặc không hợp lệ → logout");
        logout();
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [token]);

  return (
    <KeycloakContext.Provider
      value={{
        initialized,
        authenticated,
        token,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </KeycloakContext.Provider>
  );
};

export const useKeycloak = () => useContext(KeycloakContext);
